// src/pages/StaffOnboardingWizard.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import pb from '../lib/pocketbase';
import { useAuth } from '../contexts/AuthContext';
import './onboarding.css';

export default function StaffOnboardingWizard() {
  const total = 5;
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser } = useAuth();
  const [step, setStep] = useState(1);
  const [role, setRole] = useState('commercial'); // commercial | developpeur
  const [staff, setStaff] = useState({ firstName: '', lastName: '', company: '', email: '', phone: '', address: '', postalCode: '', city: '', password: '', confirm: '' });
  const [access, setAccess] = useState({ git: false, figma: false, slack: false, crm: false, laptop: true, phone: false });
  const [policies, setPolicies] = useState({ nda: false, it: false });
  const [isDark, setIsDark] = useState(false); // Simplified theme state for brevity
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isExistingAccount, setIsExistingAccount] = useState(false);

  // Smart Fallback State
  const [isLoginFallback, setIsLoginFallback] = useState(false);
  const [fallbackPassword, setFallbackPassword] = useState('');

  // Option 3: Auto-fill if logged in & Handle Jump from Dashboard
  useEffect(() => {
    if (currentUser) {
      setStaff(prev => ({
        ...prev,
        email: currentUser.email,
        firstName: currentUser.name?.split(' ')[0] || '',
        lastName: currentUser.name?.split(' ').slice(1).join(' ') || '',
      }));
      setIsExistingAccount(true); // Implicitly existing

      // Handle Skip Logic from Dashboard
      if (location.state?.role) {
        setRole(location.state.role);

        // Pre-fill staff info if provided in state
        if (location.state.staff) {
          setStaff(prev => ({ ...prev, ...location.state.staff }));
        }

        if (location.state?.skipIntro) {
          setStep(4); // Jump directly to Skills (Step 4)
        }
      }
    }
  }, [currentUser, location.state]);

  const ROLE_ICONS = {
    commercial: () => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 7h18v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
        <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      </svg>
    ),
    developpeur: () => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 5h18v14H3z" />
        <path d="M7 9l-2 3 2 3" />
        <path d="M17 9l2 3-2 3" />
        <path d="M10 15l4-6" />
      </svg>
    )
  };

  const next = () => setStep(s => Math.min(total, s + 1));
  const prev = () => setStep(s => Math.max(1, s - 1));

  // Step validation
  const [errors, setErrors] = useState({});
  const [siret, setSiret] = useState({ has: 'oui', mode: 'now', value: '' });

  const validateStep2 = () => {
    const e = {};
    const emailOk = !!staff.email && /.+@.+\..+/.test(staff.email);
    const pwOk = (staff.password || '').length >= 8;
    const confirmOk = staff.password === staff.confirm;
    if (!staff.firstName) e.firstName = 'Prénom requis';
    if (!staff.lastName) e.lastName = 'Nom requis';
    if (!emailOk) e.email = 'Email invalide';
    if (!staff.phone) e.phone = 'Téléphone requis';

    // Password validation logic
    if (!currentUser) {
      if (!staff.password) e.password = 'Mot de passe requis';
      else if (!isExistingAccount && staff.password.length < 8) e.password = '8 caractères minimum';

      if (!isExistingAccount) {
        if (!confirmOk) e.confirm = 'Les mots de passe ne correspondent pas';
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (step === 3) {
      if (!validateStep2()) return;
    }
    next();
  };

  // Skills state
  // ... (keeping simplified mostly, focusing on submit)
  const SKILLS_SALES = ['Prospection', 'Qualification', 'CRM', 'Négociation', 'Closing', 'Reporting'];
  const SKILLS_DEV = ['JavaScript/TypeScript', 'React', 'Node.js', 'SQL/NoSQL', 'DevOps', 'Tests'];
  const [skills, setSkills] = useState({ items: {}, experience: '', availability: '', education: '', workMode: 'hybride', salaryUnit: 'projet', salaryMin: '', salaryMax: '', motivations: { why: '', goals: '', achievements: '' }, freeNote: '', uploads: { cv: null, lm: null, others: [] } });

  const toggleSkill = (name) => {
    setSkills(s => ({
      ...s,
      items: { ...s.items, [name]: { selected: !(s.items[name]?.selected) } }
    }));
  };

  // SUBMIT
  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      // Logic Split:
      // A) Already Logged In (currentUser) -> Just create state
      // B) Existing Account Logic (isExistingAccount) -> Login -> Create state
      // C) New User -> Create User -> Login -> Create state

      let userId = currentUser?.id;

      if (!userId) {
        if (isExistingAccount) {
          // Option 1: Login existing
          const authData = await pb.collection('users').authWithPassword(staff.email, staff.password);
          userId = authData.record.id;
          // Update profile with phone/name if needed? skipping for now
        } else {
          // Option New: Create User
          const userData = {
            email: staff.email,
            password: staff.password,
            passwordConfirm: staff.confirm,
            name: `${staff.firstName} ${staff.lastName}`,
            emailVisibility: true,
            role: 'user',
            verified: false
          };
          const record = await pb.collection('users').create(userData);
          await pb.collection('users').authWithPassword(staff.email, staff.password);
          userId = record.id;
        }
      }

      // 3. Create Application State
      await pb.collection('onboarding_states').create({
        user: userId,
        step: `candidature_${role}`,
        status: 'pending',
        progress: 100,
        completed: false
      });

      // (Optional) Upload CV...

      alert('Candidature envoyée avec succès ! Un administrateur va examiner votre dossier.');
      navigate('/'); // Redirect to home or login

    } catch (err) {
      console.error("Registration error", err);
      let message = err.message || "Une erreur est survenue lors de l'inscription.";

      // Gestion spécifique email déjà pris
      if (err?.data?.data?.email?.code === 'validation_not_unique') {
        // Tentative de connexion automatique avec le mot de passe fourni (Smart Retry)
        try {
          console.log("Tentative de connexion (Smart Retry)...");
          const authData = await pb.collection('users').authWithPassword(staff.email, staff.password);
          // Si ça marche, on crée la candidature
          await pb.collection('onboarding_states').create({
            user: authData.record.id,
            step: `candidature_${role}`,
            status: 'pending',
            progress: 100,
            completed: false
          });
          alert('Compte existant reconnu ! Candidature envoyée avec succès.');
          navigate('/');
          return;
        } catch (loginErr) {
          // Si le mot de passe est faux, on demande le bon
          setIsLoginFallback(true);
          message = "Ce compte existe déjà. Veuillez saisir votre mot de passe pour confirmer votre identité.";
        }
      }

      setError(message);
      // Logout just in case (cleanup)
      if (!isLoginFallback) pb.authStore.clear();
    } finally {
      setLoading(false);
    }
  };

  const handleFallbackSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const authData = await pb.collection('users').authWithPassword(staff.email, fallbackPassword);
      await pb.collection('onboarding_states').create({
        user: authData.record.id,
        step: `candidature_${role}`,
        status: 'pending',
        progress: 100,
        completed: false
      });
      alert('Authentification réussie ! Candidature envoyée.');
      navigate('/');
    } catch (err) {
      setError("Mot de passe incorrect.");
      setLoading(false);
    }
  };

  return (
    <div className="onboarding-container">
      <div className="onboarding-inner">
        <div className="onboarding-head enhanced">
          <div className="head-top">
            <div className="onboarding-brand"><div className="brand-title">Candidature</div></div>
            <div className="head-actions"><a className="head-link" href="/">Retour au site</a></div>
          </div>
          <div className="head-welcome">
            <h1>Rejoindre l'équipe</h1>
            <p>Postulez en quelques étapes.</p>
          </div>
        </div>

        <div className="wizard-steps">
          {Array.from({ length: total }).map((_, i) => (
            <button key={i} type="button" className={`wizard-dot wizard-dot-btn ${step === i + 1 ? 'active' : step > i + 1 ? 'done' : ''}`} onClick={() => { }}>
              {i + 1}
            </button>
          ))}
        </div>

        <div className="onboarding-card">
          {error && <div className="alert alert-danger" style={{ marginBottom: '16px', color: 'red' }}>{error}</div>}

          {step === 1 && (
            <div>
              <h2 style={{ margin: '0 0 10px' }}>Votre rôle visé</h2>
              <div className="status-cards">
                {['commercial', 'developpeur'].map(r => (
                  <button key={r} type="button" className={`status-card ${role === r ? 'active' : ''}`} onClick={() => setRole(r)}>
                    <div className="status-icon">{(ROLE_ICONS[r] || (() => null))()}</div>
                    <div className="status-label" style={{ fontWeight: 600, textTransform: 'capitalize' }}>{r}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h2>Informations Légales (Optionnel)</h2>
              <p>Avez-vous un SIRET ? (Pour les freelances/indépendants)</p>
              <div style={{ display: 'flex', gap: 12 }}>
                <button className={`btn ${siret.has === 'oui' ? 'primary' : ''}`} onClick={() => setSiret({ ...siret, has: 'oui' })}>Oui</button>
                <button className={`btn ${siret.has === 'non' ? 'primary' : ''}`} onClick={() => setSiret({ ...siret, has: 'non' })}>Non</button>
              </div>
              {siret.has === 'oui' && (
                <input style={{ marginTop: 12, width: '100%' }} placeholder="Numéro SIRET" value={siret.value} onChange={e => setSiret({ ...siret, value: e.target.value })} />
              )}
            </div>
          )}

          {step === 3 && (
            <div>
              <h2 style={{ margin: '0 0 10px' }}>Vos informations</h2>
              <div style={{ display: 'grid', gap: 12 }}>
                {!currentUser && (
                  <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.9rem', color: 'var(--text-color)' }}>
                    <input type="checkbox" id="existingAccount" checked={isExistingAccount} onChange={e => setIsExistingAccount(e.target.checked)} style={{ width: 'auto' }} />
                    <label htmlFor="existingAccount" style={{ cursor: 'pointer', fontWeight: 600 }}>J'ai déjà un compte client</label>
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <input placeholder="Prénom *" value={staff.firstName} onChange={e => setStaff({ ...staff, firstName: e.target.value })} className={errors.firstName ? 'input-error' : ''} disabled={!!currentUser} />
                  <input placeholder="Nom *" value={staff.lastName} onChange={e => setStaff({ ...staff, lastName: e.target.value })} className={errors.lastName ? 'input-error' : ''} disabled={!!currentUser} />
                </div>
                <input type="email" placeholder="Email *" value={staff.email} onChange={e => setStaff({ ...staff, email: e.target.value })} className={errors.email ? 'input-error' : ''} disabled={!!currentUser} />
                <input placeholder="Téléphone *" value={staff.phone} onChange={e => setStaff({ ...staff, phone: e.target.value })} className={errors.phone ? 'input-error' : ''} />

                {!currentUser && (
                  <div style={{ display: 'grid', gridTemplateColumns: isExistingAccount ? '1fr' : '1fr 1fr', gap: 12 }}>
                    <input type="password" placeholder={isExistingAccount ? "Votre mot de passe de connexion *" : "Créer un mot de passe *"} value={staff.password} onChange={e => setStaff({ ...staff, password: e.target.value })} className={errors.password ? 'input-error' : ''} />
                    {!isExistingAccount && (
                      <input type="password" placeholder="Confirmer *" value={staff.confirm} onChange={e => setStaff({ ...staff, confirm: e.target.value })} className={errors.confirm ? 'input-error' : ''} />
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 4 && (
            <div>
              <h2>Profil & Compétences</h2>
              <p>Sélectionnez vos compétences principales :</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                {(role === 'commercial' ? SKILLS_SALES : SKILLS_DEV).map((name) => (
                  <button key={name} type="button"
                    className={`btn ${skills.items[name]?.selected ? 'success' : ''}`}
                    onClick={() => toggleSkill(name)}
                  >{name}</button>
                ))}
              </div>
              <textarea rows={4} placeholder="Pourquoi souhaitez-vous nous rejoindre ?" value={skills.motivations.why} onChange={e => setSkills({ ...skills, motivations: { ...skills.motivations, why: e.target.value } })} />
            </div>
          )}

          {step === 5 && (
            <div>
              <h2>Validation</h2>
              <div className="card" style={{ padding: 16, marginBottom: 16 }}>
                <p><strong>Rôle :</strong> {role.toUpperCase()}</p>
                <p><strong>Nom :</strong> {staff.firstName} {staff.lastName}</p>
                <p><strong>Email :</strong> {staff.email}</p>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 6 }}>
                <label style={{ display: 'flex', gap: 8 }}><input type="checkbox" checked={policies.nda} onChange={e => setPolicies({ ...policies, nda: e.target.checked })} /> J'accepte la politique de confidentialité</label>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 6 }}>
                <label style={{ display: 'flex', gap: 8 }}><input type="checkbox" checked={policies.it} onChange={e => setPolicies({ ...policies, it: e.target.checked })} /> Je certifie l'exactitude des informations</label>
              </div>

              {isLoginFallback && (
                <div style={{ marginTop: 16, padding: 16, background: 'rgba(var(--primary-color-rgb), 0.1)', borderRadius: 8, border: '1px solid var(--primary-color)' }}>
                  <p style={{ marginTop: 0, fontWeight: 600 }}>Confirmer votre identité pour {staff.email}</p>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <input
                      type="password"
                      placeholder="Votre mot de passe"
                      value={fallbackPassword}
                      onChange={e => setFallbackPassword(e.target.value)}
                      style={{ flex: 1 }}
                    />
                    <button className="btn primary" onClick={handleFallbackSubmit} disabled={loading}>
                      {loading ? '...' : 'Valider & Envoyer'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="wizard-actions bottom">
          <div>{step > 1 && !isLoginFallback && (<button className="btn" onClick={prev}>Retour</button>)}</div>
          <div>
            {step < total && (<button className="btn primary" onClick={handleNext}>Suivant</button>)}
            {step === total && !isLoginFallback && (
              <button
                className="btn success"
                disabled={!(policies.nda && policies.it) || loading}
                onClick={handleSubmit}
              >
                {loading ? 'Traitement...' : 'Envoyer ma candidature'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
